import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../layout/AppShell";
import {
  getPost,
  listComments,
  addComment,
  editComment,
  deleteComment,
  listPostMedia,
  deleteMedia,
  type MediaItem,
} from "../services/communityService";
import type { CommunityPost } from "../models/community";
import { useAuthStore } from "../stores/authStore";
import {
  getFirestore,
  collection,
  where,
  getDocs,
  query as fsQuery,
  documentId,
} from "firebase/firestore";
import {
  MdArrowBack,
  MdDelete,
  MdEdit,
  MdSave,
  MdClose,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import ConfirmDialog from "../components/ConfirmDialog";
import type { Role } from "../models/auth";

/* ───────────────── Roles con capacidad de moderación en detalle ─────────────
   (owner / super_admin / admin / moderator / publisher) */
const MOD_ROLES = [
  "owner",
  "super_admin",
  "admin",
  "moderator",
  "publisher",
] as const satisfies readonly Role[];

type UserBrief = { fullName?: string; email?: string };

const chunk = <T,>(arr: readonly T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

const authorLabel = (u?: UserBrief, uid?: string) =>
  u ? (
    <span>
      {u.fullName ?? "—"} <span className="opacity-70">• {u.email ?? "—"}</span>
    </span>
  ) : uid ? (
    <code>{uid.slice(0, 8)}…</code>
  ) : (
    "—"
  );

/* ───────────── Lightbox (visor de imágenes/video) ───────────── */
function Lightbox({
  items,
  index,
  onClose,
  onPrev,
  onNext,
  canModerate,
  onDelete,
}: {
  items: MediaItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  canModerate: boolean;
  onDelete: (idx: number) => void;
}): React.ReactElement {
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [drag, setDrag] = React.useState<{
    active: boolean;
    x: number;
    y: number;
  }>({ active: false, x: 0, y: 0 });

  const item = items.at(index);

  React.useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setDrag({ active: false, x: 0, y: 0 });
  }, [index, items.length]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

  const onWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
    if (!item || item.kind !== "image") return;
    e.preventDefault();
    const next = clamp(zoom + (e.deltaY > 0 ? -0.15 : 0.15), 1, 4);
    setZoom(next);
    if (next === 1) setOffset({ x: 0, y: 0 });
  };
  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!item || item.kind !== "image") return;
    e.preventDefault();
    const next = zoom === 1 ? 2 : 1;
    setZoom(next);
    if (next === 1) setOffset({ x: 0, y: 0 });
  };
  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!item || item.kind !== "image" || zoom === 1) return;
    e.preventDefault();
    setDrag({ active: true, x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!drag.active) return;
    setOffset({ x: e.clientX - drag.x, y: e.clientY - drag.y });
  };
  const onMouseUp: React.MouseEventHandler<HTMLDivElement> = () =>
    setDrag((d) => ({ ...d, active: false }));

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 grid w-[min(96vw,1100px)] -translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto] gap-3 rounded-xl border border-surface bg-surface p-3 shadow-2xl">
        {/* Header: cerrar / eliminar */}
        <div className="flex items-center justify-end gap-2">
          {canModerate && typeof index === "number" && (
            <button
              className="btn btn-outline px-3 py-1.5"
              onClick={() => onDelete(index)}
              title="Eliminar archivo"
            >
              <MdDelete />
            </button>
          )}
          <button className="btn btn-outline px-3 py-1.5" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        {/* Contenido */}
        <div className="relative flex min-h-[300px] items-center justify-center">
          <button
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
            aria-label="Anterior"
            onClick={onPrev}
          >
            <MdChevronLeft size={24} />
          </button>

          <div
            className={`max-h-[72vh] w-full max-w-[1000px] select-none ${
              item && item.kind === "image" && zoom > 1
                ? drag.active
                  ? "cursor-grabbing"
                  : "cursor-grab"
                : ""
            }`}
            onWheel={onWheel}
            onDoubleClick={onDoubleClick}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {!item ? (
              <div className="flex h-[60vh] items-center justify-center text-on-surface/70">
                Archivo no disponible
              </div>
            ) : item.kind === "image" ? (
              <img
                src={item.downloadURL}
                alt="image"
                className="mx-auto max-h-[72vh] w-auto rounded-lg object-contain will-change-transform"
                style={{
                  transform:
                    zoom === 1
                      ? "translate(0px,0px) scale(1)"
                      : `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                  transition: drag.active ? "none" : "transform 120ms ease",
                }}
                draggable={false}
              />
            ) : (
              <video
                src={item.downloadURL}
                controls
                className="mx-auto max-h-[72vh] w-full rounded-lg"
              />
            )}
          </div>

          <button
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
            aria-label="Siguiente"
            onClick={onNext}
          >
            <MdChevronRight size={24} />
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end">
          {item && (
            <a
              className="text-primary underline"
              href={item.downloadURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir en pestaña nueva
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────── Página ───────────── */
export default function PostDetail(): React.ReactElement {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const auth = useAuthStore((s) => s.state);
  const me = auth.status === "authed" ? auth.user : null;

  // Moderación en detalle: admins + moderator + publisher
  const canModerate = useAuthStore((s) => s.hasAnyRole(MOD_ROLES));

  const [post, setPost] = React.useState<CommunityPost | null>(null);
  const [people, setPeople] = React.useState<Record<string, UserBrief>>({});
  const [comments, setComments] = React.useState<
    Array<{ id: string; uid: string; text: string }>
  >([]);
  const [media, setMedia] = React.useState<MediaItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState<string | null>(null);

  const [newComment, setNewComment] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingText, setEditingText] = React.useState("");

  // Lightbox
  const [viewerIndex, setViewerIndex] = React.useState<number | null>(null);

  // Confirmaciones
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = React.useState<
    string | null
  >(null);
  const [confirmDeleteMediaIdx, setConfirmDeleteMediaIdx] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const p = await getPost(id);
        if (!mounted) return;
        setPost(p);

        const cmts = await listComments(id);
        if (!mounted) return;
        setComments(cmts.map(({ id, uid, text }) => ({ id, uid, text })));

        // carga autores de post + comentarios
        const uids = new Set<string>();
        if (p?.authorUid) uids.add(p.authorUid);
        cmts.forEach((c) => uids.add(c.uid));

        if (uids.size > 0) {
          const db = getFirestore();
          const col = collection(db, "users");
          const found: Record<string, UserBrief> = {};
          for (const ids of chunk([...uids], 10)) {
            const snap = await getDocs(
              fsQuery(col, where(documentId(), "in", ids))
            );
            snap.forEach((doc) => {
              const d = doc.data() as any;
              const brief: UserBrief = {
                ...(typeof d.fullName === "string"
                  ? { fullName: d.fullName }
                  : {}),
                ...(typeof d.email === "string" ? { email: d.email } : {}),
              };
              found[doc.id] = brief;
            });
          }
          if (!mounted) return;
          setPeople(found);
        }

        // media
        if (p?.authorUid) {
          const items = await listPostMedia(p.authorUid, id);
          setMedia(items);
        }

        setErr(null);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  const submitNewComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!me) return;
    const text = newComment.trim();
    if (!text) return;
    await addComment(id, me.uid, text);
    setComments((r) => [...r, { id: crypto.randomUUID(), uid: me.uid, text }]); // optimista
    setNewComment("");
  };

  const startEdit = (cId: string, text: string) => {
    setEditingId(cId);
    setEditingText(text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };
  const saveEdit = async (cId: string) => {
    const t = editingText.trim();
    if (!t) return;
    await editComment(id, cId, t);
    setComments((r) => r.map((c) => (c.id === cId ? { ...c, text: t } : c)));
    cancelEdit();
  };

  // Moderación de comentarios:
  // - Moderators/Publishers/Admins pueden editar/eliminar cualquiera
  // - Otros usuarios solo los propios
  const canEditOrDeleteComment = (uid: string) =>
    canModerate || (me && me.uid === uid);

  const doRemoveComment = async () => {
    if (!confirmDeleteCommentId) return;
    const cId = confirmDeleteCommentId;
    setConfirmDeleteCommentId(null);
    await deleteComment(id, cId);
    setComments((r) => r.filter((c) => c.id !== cId));
  };

  // Media delete (solo moderadores/admins/publishers)
  const askRemoveMedia = (idx: number) => {
    if (!canModerate) return;
    if (idx < 0 || idx >= media.length) return;
    setConfirmDeleteMediaIdx(idx);
  };
  const doRemoveMedia = async () => {
    if (confirmDeleteMediaIdx === null) return;
    const idx = confirmDeleteMediaIdx;
    const m = media.at(idx);
    setConfirmDeleteMediaIdx(null);
    if (!m) return;
    await deleteMedia(m.path);
    setMedia((r) => r.filter((_, i) => i !== idx));
    setViewerIndex((v) => {
      if (v === null) return v;
      if (v === idx) return null;
      return v > idx ? v - 1 : v;
    });
  };

  // Visor
  const openViewer = (idx: number) => {
    if (idx < 0 || idx >= media.length) return;
    setViewerIndex(idx);
  };
  const closeViewer = () => setViewerIndex(null);
  const prevViewer = () =>
    setViewerIndex((v) => {
      if (v === null || media.length === 0) return v;
      return (v - 1 + media.length) % media.length;
    });
  const nextViewer = () =>
    setViewerIndex((v) => {
      if (v === null || media.length === 0) return v;
      return (v + 1) % media.length;
    });

  return (
    <AppShell>
      <section className="card p-4">
        <div className="mb-3 flex items-center justify-between">
          <button
            className="btn btn-outline flex items-center gap-1"
            onClick={() =>
              window.history.length > 1 ? navigate(-1) : navigate("/community")
            }
          >
            <MdArrowBack /> Volver
          </button>
        </div>

        {loading ? (
          <p>Cargando…</p>
        ) : err ? (
          <p className="text-red-600">{err}</p>
        ) : !post ? (
          <p>No encontrado.</p>
        ) : (
          <>
            <h2 className="mb-1 text-xl font-semibold">
              {post.text?.slice(0, 80) || "Post"}
            </h2>
            <div className="mb-4 text-sm text-on-surface/70">
              Autor: {authorLabel(people[post.authorUid], post.authorUid)}
            </div>

            {/* Media */}
            {media.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-2 font-semibold">Archivos</h3>
                <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {media.map((m, idx) => (
                    <li
                      key={m.path}
                      className="group rounded-lg border border-surface p-2"
                    >
                      <button
                        type="button"
                        className="mb-2 block w-full overflow-hidden rounded"
                        onClick={() => openViewer(idx)}
                        title="Abrir visor"
                      >
                        {m.kind === "image" ? (
                          <img
                            src={m.downloadURL}
                            alt="image"
                            className="max-h-56 w-full rounded object-cover transition-transform group-hover:scale-[1.01]"
                          />
                        ) : (
                          <video
                            src={m.downloadURL}
                            controls
                            className="max-h-56 w-full rounded"
                          />
                        )}
                      </button>
                      <div className="flex items-center justify-end gap-2 text-sm">
                        <a
                          className="text-primary underline"
                          href={m.downloadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Abrir
                        </a>
                        {canModerate && (
                          <button
                            className="btn btn-outline px-2 py-1"
                            onClick={() => askRemoveMedia(idx)}
                            title="Eliminar archivo"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Comentarios */}
            <form onSubmit={submitNewComment} className="mb-4 flex gap-2">
              <input
                className="input-surface grow"
                placeholder="Escribe un comentario…"
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
              />
              <button className="btn btn-primary" type="submit">
                Comentar
              </button>
            </form>

            <div className="space-y-2">
              {comments.map((c) => {
                const author = people[c.uid];
                const allowed = canEditOrDeleteComment(c.uid);
                const isEditing = editingId === c.id;
                return (
                  <article
                    key={c.id}
                    className="rounded-lg border border-surface p-3"
                  >
                    <div className="mb-1 text-sm font-medium">
                      {author?.fullName ?? "—"}
                      <span className="opacity-70">
                        {" "}
                        • {author?.email ?? c.uid}
                      </span>
                    </div>

                    {isEditing ? (
                      <>
                        <textarea
                          className="input-surface min-h-[100px]"
                          value={editingText}
                          onChange={(e) =>
                            setEditingText(e.currentTarget.value)
                          }
                        />
                        <div className="mt-2 flex gap-2">
                          <button
                            className="btn btn-primary flex items-center gap-1"
                            type="button"
                            onClick={() => void saveEdit(c.id)}
                          >
                            <MdSave /> Guardar
                          </button>
                          <button
                            className="btn btn-outline flex items-center gap-1"
                            type="button"
                            onClick={cancelEdit}
                          >
                            <MdClose /> Cancelar
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="whitespace-pre-wrap">{c.text}</p>
                    )}

                    {allowed && !isEditing && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => startEdit(c.id, c.text)}
                        >
                          <MdEdit /> Editar
                        </button>
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => setConfirmDeleteCommentId(c.id)}
                        >
                          <MdDelete /> Eliminar
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* Lightbox */}
      {viewerIndex !== null && (
        <Lightbox
          items={media}
          index={viewerIndex}
          onClose={closeViewer}
          onPrev={prevViewer}
          onNext={nextViewer}
          canModerate={canModerate}
          onDelete={(idx) => askRemoveMedia(idx)}
        />
      )}

      {/* Confirmaciones */}
      <ConfirmDialog
        open={confirmDeleteCommentId !== null}
        title="Eliminar comentario"
        message="¿Seguro que deseas eliminar este comentario? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => void doRemoveComment()}
        onCancel={() => setConfirmDeleteCommentId(null)}
      />
      <ConfirmDialog
        open={confirmDeleteMediaIdx !== null}
        title="Eliminar archivo"
        message="¿Seguro que deseas eliminar este archivo del post? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => void doRemoveMedia()}
        onCancel={() => setConfirmDeleteMediaIdx(null)}
      />
    </AppShell>
  );
}
